import {handleActions} from 'redux-actions';
import {Map} from 'immutable';

import {combineFeedItemWithPost} from 'web-app/react/entities/feed/items/model';

import * as Actions from './actions';
import * as Helpers from './helpers';

const updateFeedItemByFindFunction = (state, findFunc, updateFunc) => {
    const updateKey = state.findKey(findFunc);
    if (!updateKey) {
        return state;
    }

    return state.update(updateKey, updateFunc);
};

const updateFeedItem = (state, id, updateFunc, key = 'originatorId') => {
    return updateFeedItemByFindFunction(state, feedItem => feedItem.get(key) === id, updateFunc);
};

export default handleActions(
    {
        [Actions.fetchSuccess.type]: (state, {payload}) =>
            state.merge(payload.feedItems.map(item => [item.feedItemId, item])),
        [Actions.fetchSingleSuccess.type]: (state, {payload}) =>
            state.set(payload.feedItem.feedItemId, payload.feedItem),
        [Actions.updateItemSuccess.type]: (state, {payload}) => {
            if (payload.isObservation) {
                return updateFeedItemByFindFunction(
                    state,
                    feedItem =>
                        Helpers.feedItemIsObservation(feedItem) &&
                        feedItem.embed.get('observationId') === payload.item.observation.id,
                    feedItem =>
                        Helpers.getObservationFeedItem(
                            feedItem,
                            [payload.item.observation],
                            feedItem.embed.get('frameworks'),
                        ),
                );
            }
            return updateFeedItem(state, payload.originatorId, feedItem =>
                combineFeedItemWithPost(feedItem, payload.item),
            );
        },
        [Actions.deleteItemSuccess.type]: (state, {payload}) =>
            state.filter(feedItem => feedItem.originatorId !== payload.originatorId),
        [Actions.deleteComment.type]: (state, {payload}) => {
            return updateFeedItem(state, payload.originatorId, feedItem =>
                feedItem.merge({
                    comments: feedItem.comments.map(comment => {
                        if (comment.commentId !== payload.commentId) {
                            return comment;
                        }

                        return comment.merge({
                            deleting: true,
                        });
                    }),
                }),
            );
        },
        [Actions.deleteCommentSuccess.type]: (state, {payload}) => {
            return updateFeedItem(state, payload.originatorId, feedItem =>
                feedItem.merge({
                    comments: feedItem.comments.filter(comment => comment.commentId !== payload.commentId),
                }),
            );
        },
        [Actions.deleteCommentFailed.type]: (state, {payload}) => {
            return updateFeedItem(state, payload.originatorId, feedItem =>
                feedItem.merge({
                    comments: feedItem.comments.map(comment => {
                        if (comment.commentId !== payload.commentId) {
                            return comment;
                        }

                        return comment.merge({
                            deleting: false,
                        });
                    }),
                }),
            );
        },
        [Actions.deleteCommentAborted.type]: (state, {payload}) => {
            return updateFeedItem(state, payload.originatorId, feedItem =>
                feedItem.merge({
                    comments: feedItem.comments.map(comment => {
                        if (comment.commentId !== payload.commentId) {
                            return comment;
                        }

                        return comment.merge({
                            deleting: false,
                        });
                    }),
                }),
            );
        },
        [Actions.likeCommentSuccess.type]: (state, {payload}) => {
            return updateFeedItem(state, payload.originatorId, feedItem =>
                feedItem.merge({
                    comments: feedItem.comments.map(comment => {
                        if (comment.commentId !== payload.commentId) {
                            return comment;
                        }

                        return comment.merge({
                            liked: payload.like,
                        });
                    }),
                }),
            );
        },
        [Actions.likeSuccess.type]: (state, {payload}) => {
            return updateFeedItem(state, payload.originatorId, feedItem =>
                feedItem.merge({
                    liked: true,
                }),
            );
        },
        [Actions.unlikeSuccess.type]: (state, {payload}) => {
            return updateFeedItem(state, payload.originatorId, feedItem =>
                feedItem.merge({
                    liked: false,
                }),
            );
        },
        [Actions.comment.type]: (state, {payload}) => {
            return updateFeedItem(state, payload.originatorId, feedItem =>
                feedItem.merge({
                    commenting: true,
                }),
            );
        },
        [Actions.commentSuccess.type]: (state, {payload}) => {
            return updateFeedItem(state, payload.originatorId, feedItem =>
                feedItem.merge({
                    commenting: false,
                    comments: feedItem.comments.push(payload.comment),
                }),
            );
        },
        [Actions.commentAborted.type]: (state, {payload}) => {
            return updateFeedItem(state, payload.originatorId, feedItem =>
                feedItem.merge({
                    commenting: false,
                }),
            );
        },
        [Actions.commentFailed.type]: (state, {payload}) => {
            return updateFeedItem(state, payload.originatorId, feedItem =>
                feedItem.merge({
                    commenting: false,
                }),
            );
        },
        [Actions.fetchCommentsAndLikesSuccess.type]: (state, {payload}) => {
            return updateFeedItem(state, payload.originatorId, feedItem => {
                const newItem = feedItem.merge({
                    likes: payload.likes,
                    comments: payload.comments,
                });
                return newItem;
            });
        },
        [Actions.bookmarkItem.type]: (state, {payload}) => {
            return updateFeedItem(
                state,
                payload.feedItemId,
                feedItem =>
                    feedItem.merge({
                        bookmarking: true,
                    }),
                'feedItemId',
            );
        },
        [Actions.bookmarkItemSuccess.type]: (state, {payload}) => {
            return updateFeedItem(
                state,
                payload.feedItemId,
                feedItem =>
                    feedItem.merge({
                        bookmarked: true,
                        bookmarking: false,
                    }),
                'feedItemId',
            );
        },
        [Actions.bookmarkItemFailed.type]: (state, {payload}) => {
            return updateFeedItem(
                state,
                payload.feedItemId,
                feedItem =>
                    feedItem.merge({
                        bookmarking: false,
                    }),
                'feedItemId',
            );
        },
        [Actions.unBookmarkItemAborted.type]: (state, {payload}) => {
            return updateFeedItem(
                state,
                payload.feedItemId,
                feedItem =>
                    feedItem.merge({
                        bookmarking: false,
                    }),
                'feedItemId',
            );
        },
        [Actions.unBookmarkItemFailed.type]: (state, {payload}) => {
            return updateFeedItem(
                state,
                payload.feedItemId,
                feedItem =>
                    feedItem.merge({
                        bookmarking: false,
                    }),
                'feedItemId',
            );
        },
        [Actions.unBookmarkItem.type]: (state, {payload}) => {
            return updateFeedItem(
                state,
                payload.feedItemId,
                feedItem =>
                    feedItem.merge({
                        bookmarking: true,
                    }),
                'feedItemId',
            );
        },
        [Actions.unBookmarkItemSuccess.type]: (state, {payload}) => {
            return updateFeedItem(
                state,
                payload.feedItemId,
                feedItem =>
                    feedItem.merge({
                        bookmarked: false,
                        bookmarking: false,
                    }),
                'feedItemId',
            );
        },
    },
    new Map(),
);
